import { Component, Input, OnInit } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { Observable, Subscription, map, switchMap, take, tap } from 'rxjs';
import { saveAs } from 'file-saver-es';
import { Router } from '@angular/router';
import { AdminUsersRoutes, UserUsersAndPaxRoutes } from 'src/app/core/enums/routes.enum';
import { DepartmentState } from 'src/app/department/state/department.state';
import { IdName } from 'src/app/shared/models/id-name';
import { LoadDepartmentsAccessedByEmployment } from 'src/app/department/state/department.actions';
import { LoadAllShiftsWithDepartment } from 'src/app/crew-management/state/shifts/shift.actions';
import { ShiftState } from 'src/app/crew-management/state/shifts/shift.state';
import { FormControl } from '@angular/forms';
import { ToastService } from 'src/app/core/services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { IShiftWithDepartment } from 'src/app/crew-management/models/shift.model';
import { endOfDay } from 'date-fns';
import { datepickerValueToDate } from 'src/app/shared/functions/datepicker-value-to-date';
import { FormDataService } from 'src/app/shared/attachments/service/form-data.service';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { LoadTrips } from 'src/app/crew-management-user/state/trips/trip.actions';
import { ITripResponse } from 'src/app/crew-management-user/models/trip.model';
import { TripState } from 'src/app/crew-management-user/state/trips/trip.state';
import { MenuListLogicService } from 'src/app/core/services/menu-list-logic.service';
import { CompetenceMatrixState } from '../competence-matrix.state';
import {
  LoadCompetenceMatrix,
  PrintCompetenceMatrix,
  SendCompetenceMatrixNotifications,
} from '../competence-matrix.actions';
import {
  CompetenceMatrixColor,
  CompetenceMatrixPlanningModes,
  ICompetenceMatrixParams,
  ICompetenceMatrixUser,
  ICompetenceSummaryItem,
} from '../competence-matrix.model';
import { PrintCompetenceMatrixDialogComponent } from '../print-competence-matrix-dialog/print-competence-matrix-dialog.component';
import { SendCompetenceMatrixNotificationsDialogComponent } from '../send-competence-matrix-notifications-dialog/send-competence-matrix-notifications-dialog.component';

export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 0,
  position: 'above',
};

@Component({
  selector: 'app-competence-matrix',
  templateUrl: './competence-matrix.component.html',
  styleUrls: ['./competence-matrix.component.scss'],
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults }],
  standalone: false,
})
export class CompetenceMatrixComponent implements OnInit {
  @Input() inDialog = false;
  @Input() planningMode: CompetenceMatrixPlanningModes | false = false;
  @Input() showTripFilter = false;
  @Input() tripId: string;
  @Input() departmentIds: string[] = [];
  @Input() allDepartments = false;
  departments$: Observable<IdName[]> = this.store.select(
    DepartmentState.getDepartmentsAccessedByEmployment
  );
  shifts$: Observable<IShiftWithDepartment[]> = this.store
    .select(ShiftState.getAllShiftsWithDepartment)
    .pipe(
      map((shifts) => {
        return this.departmentControl.value
          ? shifts.filter((s) => s.departmentId === this.departmentControl.value)
          : [];
      })
    );
  trips$: Observable<ITripResponse[]> = this.store.select(TripState.getTrips);
  matrix$ = this.store.select(CompetenceMatrixState.matrix);

  departmentControl = new FormControl('');
  shiftControl = new FormControl({ value: '', disabled: true });
  tripControl = new FormControl('');
  excludedUsersControl = new FormControl([]);
  excludedCompetencesControl = new FormControl([]);
  userOptions: IdName[] = [];
  competenceOptions: IdName[] = [];
  pivotDateControl = new FormControl(new Date());
  users: ICompetenceMatrixUser[];
  competences: { id: string; name: string; code: string }[] = [];
  summary: ICompetenceSummaryItem[] = [];
  selectedRow: number | null = null;
  selectedColumn: number | null = null;
  colors = CompetenceMatrixColor;
  planningModes = CompetenceMatrixPlanningModes;
  isMinifiedView = false;
  showDepartmentFilter = true;
  showDateFilter = true;
  showShiftFilter = true;
  competenceNamesMap: Record<string, string> = {};
  loadingAction = [LoadCompetenceMatrix];
  printAction = [PrintCompetenceMatrix];
  shiftsLoadingAction = [LoadAllShiftsWithDepartment];
  tripsLoadingAction = [LoadTrips];
  departmentsLoadingAction = [LoadDepartmentsAccessedByEmployment];
  isInAdmin: boolean;

  private subscription = new Subscription();

  constructor(
    private store: Store,
    private router: Router,
    private toast: ToastService,
    private dialog: MatDialog,
    private menuListLogicService: MenuListLogicService
  ) {
    this.isInAdmin = this.menuListLogicService.isCurrentUrlInAdmin();
  }

  ngOnInit(): void {
    if (this.planningMode) {
      this.departmentControl.setValue(getDepartmentId());
      this.showDepartmentFilter = false;
      this.showShiftFilter = false;
      this.showDateFilter = this.planningMode === CompetenceMatrixPlanningModes.PlannedActivities;
    }

    if (this.tripId) {
      this.tripControl.setValue(this.tripId);
    }
    if (this.tripId || this.showTripFilter) {
      this.subscription.add(
        this.store
          .dispatch(new LoadTrips())
          .pipe(
            tap(() => {
              this.onTripSelected();
            })
          )
          .subscribe()
      );
    } else {
      this.loadMatrix();
    }
    this.store.dispatch([new LoadDepartmentsAccessedByEmployment()]);
  }

  loadMatrix(): void {
    let params: ICompetenceMatrixParams = {
      shiftId: this.shiftControl.value || '',
      tripId: this.tripControl.value || '',
      onlyConfirmedOnBoard: this.planningMode === CompetenceMatrixPlanningModes.CurrentPersonnel,
      onlyInPlannedActivity: !!this.planningMode,
    };
    params = this.setDepartmentIds(params);
    if (this.excludedUsersControl.value?.length) {
      params.excludedUserIds = this.excludedUsersControl.value;
    }
    if (this.excludedCompetencesControl.value?.length) {
      params.excludedCompetences = this.excludedCompetencesControl.value;
    }
    if (this.pivotDateControl.value) {
      params.pivotDate = FormDataService.dateToFormDataString(
        endOfDay(datepickerValueToDate(this.pivotDateControl.value as Date))
      );
    }
    this.store
      .dispatch(new LoadCompetenceMatrix(params, !this.planningMode))
      .pipe(
        switchMap(() => this.matrix$),
        take(1),
        tap((matrix) => {
          if (!matrix) {
            return;
          }
          this.users = matrix.result.table.usersData.map((u) => {
            return {
              ...u,
              allowNotifications: u.competenceStatuses.some(
                (s) =>
                  s.statusType === CompetenceMatrixColor.Red ||
                  s.statusType === CompetenceMatrixColor.Yellow
              ),
            };
          });
          this.summary = matrix.result.summary.competencesSummaryItems;
          this.competences = matrix.allCompetences;
          this.competences.forEach((c) => {
            this.competenceNamesMap[c.id] = c.name.toLocaleUpperCase();
          });
          if (!this.userOptions.length) {
            this.userOptions = this.users.map((u) => ({ id: u.userId, name: u.fullName }));
          }
          if (!this.competenceOptions.length) {
            this.competenceOptions = this.competences.map((c) => ({ id: c.id, name: c.name }));
          }
        })
      )
      .subscribe();
  }

  onDepartmentSelected(): void {
    this.shiftControl.setValue('');
    this.shiftControl.enable();
    this.clearUserOptions();
    this.clearCompetenceOptions();
    this.store.dispatch(new LoadAllShiftsWithDepartment());
    this.loadMatrix();
  }

  onShiftSelected(): void {
    this.clearUserOptions();
    this.clearCompetenceOptions();
    this.loadMatrix();
  }

  onTripSelected(): void {
    this.setPivotDateFromTrip();
    this.clearUserOptions();
    this.clearCompetenceOptions();
    this.loadMatrix();
  }

  setPivotDateFromTrip(): void {
    const trip = this.store
      .selectSnapshot(TripState.getTrips)
      .find((t) => t.id === this.tripControl.value);
    if (trip && trip.to) {
      this.pivotDateControl.setValue(new Date(trip.to));
    }
  }

  excludeUsers(): void {
    this.loadMatrix();
  }

  excludeCompetences(): void {
    this.loadMatrix();
  }

  clearExcludedUsers(): void {
    this.excludedUsersControl.setValue([]);
    this.loadMatrix();
  }

  clearExcludedCompetences(): void {
    this.excludedCompetencesControl.setValue([]);
    this.loadMatrix();
  }

  clearDepartment(): void {
    this.departmentControl.setValue('');
    this.shiftControl.setValue('');
    this.shiftControl.disable();
    this.clearUserOptions();
    this.clearCompetenceOptions();
    this.loadMatrix();
  }

  clearShift(): void {
    this.shiftControl.setValue('');
    this.clearUserOptions();
    this.clearCompetenceOptions();
    this.loadMatrix();
  }

  clearTrip(): void {
    this.tripControl.setValue('');
    this.pivotDateControl.setValue(new Date());
    this.clearUserOptions();
    this.clearCompetenceOptions();
    this.loadMatrix();
  }

  clearUserOptions(): void {
    this.excludedUsersControl.setValue([]);
    this.userOptions = [];
  }

  clearCompetenceOptions(): void {
    this.excludedCompetencesControl.setValue([]);
    this.competenceOptions = [];
  }

  changeDate(): void {
    this.clearUserOptions();
    this.loadMatrix();
  }

  print(): void {
    let dialogParams: any = {
      userOptions: this.userOptions,
      competenceOptions: this.competenceOptions,
      date: this.pivotDateControl.value,
      excludedCompetences: this.excludedCompetencesControl.value,
      excludedUsers: this.excludedUsersControl.value,
    };
    dialogParams = this.setDepartmentIds(dialogParams);
    this.dialog
      .open(PrintCompetenceMatrixDialogComponent, {
        width: '500px',
        panelClass: 'mobile-sizing-for-mat-dialogs',
        data: dialogParams,
      })
      .afterClosed()
      .subscribe((result: ICompetenceMatrixParams) => {
        if (!result || Object.keys(result).length === 0) {
          return;
        }
        let printParams = {
          ...result,
          shiftId: this.shiftControl.value || '',
          tripId: this.tripControl.value || '',
          pivotDate: FormDataService.dateToFormDataString(
            endOfDay(datepickerValueToDate(result.pivotDate as Date))
          ),
          onlyConfirmedOnBoard:
            this.planningMode === CompetenceMatrixPlanningModes.CurrentPersonnel,
          onlyInPlannedActivity: !!this.planningMode,
        };
        printParams = this.setDepartmentIds(printParams);
        this.toast.loading('startedGeneratingPrintFile');
        this.store
          .dispatch(new PrintCompetenceMatrix(printParams))
          .pipe(
            switchMap(() => this.store.select(CompetenceMatrixState.matrixFile)),
            tap((file) => {
              if (file) {
                if (printParams.fileType === 'excel') {
                  saveAs(file, 'Competence Matrix');
                  this.toast.success('printFileGenerated');
                } else {
                  saveAs(file, 'Competence Matrix.pdf');
                  this.toast.success('printFileGenerated');
                }
              }
            }),
            take(1)
          )
          .subscribe();
      });
  }

  navigateToUser(user: ICompetenceMatrixUser): void {
    this.router.navigateByUrl(
      this.isInAdmin
        ? `${AdminUsersRoutes.Users}/${user.userId}/(detailsOut:competence)`
        : `${UserUsersAndPaxRoutes.Users}/${user.userId}/(detailsOut:competence)`
    );
    if (this.inDialog) {
      this.dialog.closeAll();
    }
  }

  sendNotifications(): void {
    this.dialog
      .open(SendCompetenceMatrixNotificationsDialogComponent, {
        width: '500px',
        panelClass: 'mobile-sizing-for-mat-dialogs',
        data: { users: this.users.filter((u) => u.allowNotifications) },
      })
      .afterClosed()
      .subscribe((result: { userIds: string[] }) => {
        if (!result?.userIds || result.userIds.length === 0) {
          return;
        }
        this.dispatchNotificationsAction(result.userIds);
      });
  }

  sendSingleNotification(user: ICompetenceMatrixUser): void {
    this.toast.loading('sendingCompetenceMatrixNotifications');
    this.dispatchNotificationsAction([user.userId]);
  }

  private dispatchNotificationsAction(userIds: string[]): void {
    this.store.dispatch(
      new SendCompetenceMatrixNotifications({
        userIds,
        departmentId: this.departmentControl.value || '',
        shiftId: this.shiftControl.value || '',
        timeZoneOffsetInHours: new Date().getTimezoneOffset() / 60,
      })
    );
  }

  private setDepartmentIds(params: any): any {
    const newParams = { ...params };
    if (this.allDepartments) {
      newParams.departmentIds = this.departmentIds;
    } else {
      newParams.departmentId = this.departmentControl.value || '';
    }
    return newParams;
  }
}
